import React, { useEffect, useRef, useState } from 'react'

/**
 * PlacesAutocomplete component that provides Google Places autocomplete functionality.
 * Gracefully degrades to a regular text input if Google Maps API is not available.
 * 
 * Required environment variable: REACT_APP_GOOGLE_PLACE_API
 * If not set, the component will work as a regular text input without autocomplete.
 * 
 * @param {Object} validation - Formik validation object
 * @param {Function} setSelectedAddress - Callback to set the selected address
 * @param {string} placeholder - Placeholder text for the input
 * @param {string} value - Current value of the input
 */
const PlacesAutocomplete = ({
  validation,
  setSelectedAddress,
  placeholder,
  value
}) => {
  const [selectedPlace, setSelectedPlace] = useState(
    value || (validation ? validation.values.address : '')
  )
  const [addressError, setAddressError] = useState('')
  const inputRef = useRef(null)

  // Handle input changes - works for both autocomplete and manual input
  const handleInputChange = (e) => {
    const address = e.target.value
    setSelectedPlace(address)
    setSelectedAddress(address)
    // Clear any autocomplete completeness error once the user edits the field
    setAddressError('')
    // Use Formik's handleChange to maintain consistency with form validation
    if (validation?.handleChange) {
      validation.handleChange(e)
    }
  }

  // Returns the first address component matching the given Google place type
  const getComponent = (components, type) =>
    components?.find((c) => c.types.includes(type))

  // A complete address must include a street number, street name, and postal code
  // (i.e. not just a city or province).
  const isCompleteAddress = (place) => {
    const components = place.address_components
    return Boolean(
      getComponent(components, 'street_number') &&
      getComponent(components, 'route') &&
      getComponent(components, 'postal_code')
    )
  }

  useEffect(() => {
    // Check if Google API key is available
    if (!process.env.REACT_APP_GOOGLE_PLACE_API) {
      console.warn('Google Places API key is not configured. Address autocomplete will be disabled.')
      return
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_PLACE_API}&libraries=places`
    script.async = true
    script.onload = () => {
      initAutocomplete()
    }
    script.onerror = () => {
      console.error('Failed to load Google Maps API. Address autocomplete will be disabled.')
    }

    document.head.appendChild(script)

    return () => {
      // Only remove script if it was successfully added
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  const initAutocomplete = () => {
    const input = inputRef.current

    if (!input) return

    // Check if Google Maps API is available
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      console.error('Google Maps API is not available. Address autocomplete will be disabled.')
      return
    }

    const autocomplete = new window.google.maps.places.Autocomplete(input, {
      fields: ['formatted_address', 'geometry', 'name', 'address_components'],
      componentRestrictions: { country: ['ca'] },
      // Only suggest precise street addresses, so cities/provinces/regions
      // do not appear as selectable options in the dropdown.
      types: ['address'],
      strictBounds: false,
    })

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace()

      if (!place.geometry || !place.geometry.location) {
        console.error('No details available for input:', place.name)
        return
      }

      // Reject selections that are missing a street number or postal code
      // (e.g. a bare city or province) so only full addresses are stored.
      if (!isCompleteAddress(place)) {
        setAddressError(
          'Please select a complete street address including a postal code (not just a city or province).'
        )
        setSelectedAddress('')
        return
      }

      setAddressError('')
      setSelectedPlace(place?.formatted_address)
      setSelectedAddress(place?.formatted_address)

      // You can perform any other actions with the selected place data here
    })
  }

  return (
    <div>
      <input
        id='pac-input'
        ref={inputRef}
        type='text'
        placeholder={`${placeholder ? placeholder : 'Enter a location'}`}
        className={`form-control ${(validation?.touched?.address && validation?.errors?.address) || addressError
            ? 'is-invalid'
            : ''
          }`}
        value={selectedPlace}
        onChange={handleInputChange}
        onBlur={validation?.handleBlur}
      />
      {validation?.touched?.address && validation?.errors?.address && (
        <div className='invalid-feedback'>{validation?.errors?.address}</div>
      )}
      {addressError && (
        <div className='invalid-feedback' style={{ display: 'block' }}>
          {addressError}
        </div>
      )}
    </div>
  )
}
export default PlacesAutocomplete
